import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { adoIntegrationService } from '../../services/adoIntegrationService';

const SESSION_RETURN_KEY = 'ado_oauth_return_to';
const REDIRECT_PATH = '/auth/ado-callback';

/**
 * Landing page for the Azure DevOps OAuth callback.
 *
 * Microsoft redirects the browser back here with `code` + `state` query
 * params. We forward both to the backend, which validates the state,
 * exchanges the code, and persists encrypted tokens. Then we send the
 * user back to whichever Archie page initiated the flow.
 */
const AdoOAuthCallbackPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // The full-page redirect from Microsoft wipes the in-memory access
    // token. Wait for AuthContext to restore it from the refresh cookie
    // before calling the protected exchange endpoint.
    if (isLoading) {
      return;
    }
    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error_description') || params.get('error');
    if (oauthError) {
      setError(oauthError);
      return;
    }
    if (!code || !state) {
      setError('Missing authorization code or state');
      return;
    }
    if (!isAuthenticated) {
      setError(
        'Your Archie session expired during the redirect. Please sign in again and retry.',
      );
      return;
    }
    const redirectUri = `${globalThis.location.origin}${REDIRECT_PATH}`;
    void adoIntegrationService
      .exchangeConnection(code, state, redirectUri)
      .then(() => {
        setDone(true);
        const back = globalThis.sessionStorage.getItem(SESSION_RETURN_KEY);
        globalThis.sessionStorage.removeItem(SESSION_RETURN_KEY);
        navigate(back ?? '/azure-devops', { replace: true });
      })
      .catch((err: Error) => setError(err.message));
  }, [params, navigate, isAuthenticated, isLoading]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-8">
      <div className="card bg-base-200 shadow-xl max-w-lg w-full">
        <div className="card-body">
          <h2 className="card-title">Connecting Azure DevOps…</h2>
          {error ? (
            <div className="alert alert-error">{error}</div>
          ) : done ? (
            <p>Connected. Redirecting…</p>
          ) : (
            <p>Validating the response from Microsoft.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdoOAuthCallbackPage;
export { SESSION_RETURN_KEY, REDIRECT_PATH };
